import React from "react";
import {
  useViewport,
  NewRadioGroup,
  OverlayProvider,
  NewSelect,
} from "@bca/ui-library";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  StyledCompanyDetils,
  StyledContent,
  StyledTitle,
  StyledSection,
  StyledInputGroup,
  StyledInput,
  StyledRadioContainer,
  StyledRadioGroup,
  StyledRequired,
  StyledRadioLabel,
  StyledCheckboxGroup,
  StyledCheckbox,
  StyledCheckboxLabel,
  StyledHoverSpan,
  StyledDeliveryAddressGroup,
  StyledPhoneNumberGroup,
  StyledNewSelect,
  StyledRadioButtonGroup,
  StyledDisabledInput,
  StyledStateSelect,
  StyledTypography,
} from "./CompanyDetails.styled";
import Button from "../../components/Button/Button";
import WebStepper from "../../components/WebStepper/WebStepper";
import {
  getCompanyDetails,
  verifyEuVatNumber,
  changeIsEUVatNumberCheckedAndCorrect,
} from "../../reducers/companyDetailsSlice";
import {
  changeCity,
  changeCompanyPhoneNumber,
  changeCompanySiteOrPortal,
  changeDeliveryAddressConfirm,
  changeDeliveryCity,
  changeDeliveryCountry,
  changeDeliverySameAsAddress,
  changeDeliveryStreetNameAndNumber,
  changeDeliveryZipCode,
  changeEuVatTax,
  changeLocalTaxNo,
  changeCompanyPhoneNumberPrefix,
  changePostalCity,
  changePostalCountry,
  changePostalSameAsAddress,
  changePostalStreetNameAndNumber,
  changePostalZipCode,
  changeStreetNameAndNumber,
  changeZipCode,
  changeStates,
} from "../../reducers/formSlice";
import TaxNumberInvalidModal from "../../components/TaxNumberInvalidModal/TaxNumberInvalidModal";
import AddressConfirmationModal from "../../components/AddressConfirmationModal/AddressConfirmationModal";
import fetchMobileStepper from "../../utils/fetchMobileStepper";
import CircularStepper from "../../components/CircularStepper/CircularStepper";
import {
  COMPANY_DETAILS_PATH,
  COMPANY_INFORMATION_PATH,
  HOME_PATH,
} from "../../constants/paths";
import { setShowServerErrorMessage } from "../../reducers/errorManagementSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const CompanyDetails = () => {
  const { page_content, loading, isEUVatNumberCheckedAndCorrect } = useSelector(
    (state) => state.companyDetailsScreen
  );

  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );
  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const dispatch = useDispatch();

  const [companyDetails, setCompanyDetails] = React.useState({
    euVatNumber: formState?.eu_vat_tax.value || "",
    localVatNumber: formState?.local_tax_no.value || "",
    streetNameAndNumber: formState?.street.value || "",
    city: formState?.city.value || "",
    zipCode: formState?.zip_code.value || "",
    country: formState?.country.value || "",
    deliverySameAsAddress: formState?.delivery.same_as_address.value || "",
    deliveryStreetNameAndNumber: formState?.delivery.street.value || "",
    deliveryCity: formState?.delivery.city.value || "",
    deliveryZipCode: formState?.delivery.zip_code.value || "",
    deliveryCountry: formState?.delivery.country.value || "",
    postalSameAsAddress: formState?.postal.same_as_address.value || null,
    postalStreetNameAndNumber: formState?.postal.street.value || "",
    postalCity: formState?.postal.city.value || "",
    postalZipCode: formState?.postal.zip_code.value || "",
    postalCountry: formState?.postal.country.value || "",
    companyPhoneNumber: formState?.company_phone_number.value || "",
    companySiteOrPortal: formState?.company_site_or_portal.value || "",
  });
  const [continueButtonDisabled, setContinueButtonDisabled] = React.useState(
    page_content?.continue_button?.disable || true
  );

  const [mobileStepper, setMobileStepper] = React.useState();

  const [euVatTaxVerified, setEuVatTaxVerified] = React.useState(true);

  const [phoneNumberVerified, setPhoneNumberVerified] = React.useState(true);

  const [bothVatNumbersAreMandatory, setBothVatNumbersAreMandatory] =
    React.useState(false);

  const [isEuVatNumberCorrect, setIsEuVatNumberCorrect] = React.useState(true);
  const [euVatHasBeenVerified, setEuVatHasBeenVerified] = React.useState(false);

  const [showInvalidTaxNumberModal, setShowInvalidTaxNumberModal] =
    React.useState(false);

  const [confirmationModal, setConfirmationModal] = React.useState(false);

  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  React.useEffect(() => {
    if (
      page_content?.eu_vat_tax?.required &&
      page_content?.local_tax_no?.required
    ) {
      setBothVatNumbersAreMandatory(true);
    }
  }, [
    page_content?.eu_vat_tax?.required,
    page_content?.local_tax_no?.required,
  ]);

  React.useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(getCompanyDetails(countrySelected, languageCode));

    const getResult = async () => {
      const result = await fetchMobileStepper(languageCode, setMobileStepper);
      dispatch(setShowServerErrorMessage(result.responseStatus));
    };
    getResult();
  }, [countrySelected, dispatch, languageCode]);

  React.useEffect(() => {
    const vatValidation = () => {
      let verified = false;
      if (
        page_content?.eu_vat_tax?.required === true &&
        page_content?.local_tax_no?.required === false &&
        euVatTaxVerified &&
        euVatHasBeenVerified
      ) {
        verified = true;
      }

      if (
        page_content?.eu_vat_tax?.required === true &&
        euVatTaxVerified &&
        !euVatHasBeenVerified
      ) {
        verified =
          page_content?.local_tax_no?.required === true &&
          companyDetails.localVatNumber !== "";
      }

      if (
        page_content?.local_tax_no?.required === true &&
        companyDetails.localVatNumber !== ""
      ) {
        verified = true;
      }
      return verified;
    };
    if (
      (vatValidation() ||
        (!bothVatNumbersAreMandatory && isEUVatNumberCheckedAndCorrect)) &&
      (page_content?.street_name_and_number?.required === true
        ? companyDetails.streetNameAndNumber !== ""
        : true) &&
      (page_content?.city?.required === true
        ? companyDetails.city !== ""
        : true) &&
      (page_content?.zip_code?.required === true
        ? companyDetails.zipCode !== ""
        : true) &&
      (page_content?.country?.required === true
        ? companyDetails.country !== ""
        : true) &&
      (formState.delivery.same_as_address.value === "no"
        ? (page_content?.street_name_and_number?.required === true
            ? companyDetails.deliveryStreetNameAndNumber !== ""
            : true) &&
          (page_content?.city?.required === true
            ? companyDetails.deliveryCity !== ""
            : true) &&
          (page_content?.zip_code?.required === true
            ? companyDetails.deliveryZipCode !== ""
            : true) &&
          (page_content?.country?.required === true
            ? companyDetails.deliveryCountry !== ""
            : true) &&
          formState?.delivery.address_confirm.value
        : true) &&
      (formState.postal.same_as_address.value === "no"
        ? (page_content?.street_name_and_number?.required === true
            ? companyDetails.postalStreetNameAndNumber !== ""
            : true) &&
          (page_content?.city?.required === true
            ? companyDetails.postalCity !== ""
            : true) &&
          (page_content?.zip_code?.required === true
            ? companyDetails.postalZipCode !== ""
            : true) &&
          (page_content?.country?.required === true
            ? companyDetails.postalCountry !== ""
            : true)
        : true) &&
      (page_content?.company_phone_number?.required === true
        ? companyDetails.companyPhoneNumber !== ""
        : true) &&
      (page_content?.company_site_or_portal?.required === true
        ? companyDetails.companySiteOrPortal !== ""
        : true) &&
      formState?.postal.same_as_address.value &&
      phoneNumberVerified
    ) {
      setContinueButtonDisabled(false);
    } else {
      setContinueButtonDisabled(true);
    }
  }, [
    bothVatNumbersAreMandatory,
    companyDetails.city,
    companyDetails.companyPhoneNumber,
    companyDetails.companySiteOrPortal,
    companyDetails.country,
    companyDetails.deliveryCity,
    companyDetails.deliveryCountry,
    companyDetails.deliveryStreetNameAndNumber,
    companyDetails.deliveryZipCode,
    companyDetails.localVatNumber,
    companyDetails.postalCity,
    companyDetails.postalCountry,
    companyDetails.postalStreetNameAndNumber,
    companyDetails.postalZipCode,
    companyDetails.streetNameAndNumber,
    companyDetails.zipCode,
    euVatHasBeenVerified,
    euVatTaxVerified,
    isEUVatNumberCheckedAndCorrect,
    formState.delivery.address_confirm.value,
    formState.delivery.same_as_address.value,
    formState.postal.same_as_address.value,
    isEuVatNumberCorrect,
    page_content?.city?.required,
    page_content?.company_phone_number?.required,
    page_content?.company_site_or_portal?.required,
    page_content?.country?.required,
    page_content?.eu_vat_tax?.required,
    page_content?.local_tax_no?.required,
    page_content?.street_name_and_number?.required,
    page_content?.zip_code?.required,
    phoneNumberVerified,
  ]);

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "2",
          stepName: "step 2 company details",
        },
        pageInfo: {
          pageTitle: "Company Details",
          pagePath: `/${COMPANY_DETAILS_PATH}`,
          previousPagePath: `/${COMPANY_INFORMATION_PATH}`,
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "2",
          stepName: "step 2 company details",
        },
        pageInfo: {
          pageTitle: "Company Details",
          pagePath: `/${COMPANY_DETAILS_PATH}`,
          previousPagePath: `/${COMPANY_INFORMATION_PATH}`,
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode]);

  return (
    <OverlayProvider>
      {showInvalidTaxNumberModal && (
        <TaxNumberInvalidModal
          isMobile={isMobile}
          setShowInvalidTaxNumberModal={() =>
            setShowInvalidTaxNumberModal(false)
          }
        />
      )}
      {confirmationModal && (
        <AddressConfirmationModal
          setCloseModal={() => setConfirmationModal(false)}
          isOpen={confirmationModal}
          isMobile={isMobile}
          title={page_content?.terms_agree_title?.text}
          body={page_content?.terms_agree_block}
          agreeButton={page_content?.terms_agree_accept_button?.label}
          cancelButton={page_content?.terms_agree_cancel_button?.label}
        />
      )}
      <StyledCompanyDetils>
        {!loading && (
          <>
            <StyledContent>
              {!isMobile && (
                <>
                  <WebStepper activeStep={1} />
                  <StyledTitle variant="h1">{page_content?.header}</StyledTitle>
                </>
              )}
              {isMobile && (
                <CircularStepper
                  mobileStepper={mobileStepper}
                  activeStepIndex={1}
                />
              )}
              <StyledSection isMobile={isMobile}>
                <StyledInputGroup isMobile={isMobile}>
                  {page_content?.local_tax_no && (
                    <StyledInput
                      data-cy="company-details-local-tax-no"
                      state="default"
                      colorOn="bg_white"
                      label={page_content?.local_tax_no?.label}
                      required={page_content?.local_tax_no?.required}
                      value={companyDetails.localVatNumber}
                      onChange={(e) =>
                        setCompanyDetails((prevState) => ({
                          ...prevState,
                          localVatNumber: e.target.value,
                        }))
                      }
                      onBlur={(e) => dispatch(changeLocalTaxNo(e.target.value))}
                    />
                  )}
                  {bothVatNumbersAreMandatory && (
                    <StyledTypography>
                      {page_content?.eu_vat_tax_info_text?.text}
                    </StyledTypography>
                  )}
                  {page_content?.eu_vat_tax && (
                    <StyledInput
                      data-cy="company-details-eu-vat-tax"
                      state={
                        euVatTaxVerified && isEuVatNumberCorrect
                          ? "default"
                          : "error"
                      }
                      message={
                        (euVatTaxVerified
                          ? ""
                          : page_content?.eu_vat_tax?.error_message) ||
                        (euVatTaxVerified && isEuVatNumberCorrect
                          ? ""
                          : page_content?.eu_vat_tax_invalid_text?.text)
                      }
                      colorOn="bg_white"
                      label={page_content?.eu_vat_tax?.label}
                      required={
                        page_content?.eu_vat_tax?.required &&
                        page_content?.local_tax_no?.required
                          ? false
                          : page_content?.eu_vat_tax?.required
                      }
                      value={companyDetails.euVatNumber}
                      onChange={(e) => {
                        dispatch(changeIsEUVatNumberCheckedAndCorrect(false));
                        let regex;
                        setCompanyDetails((prevState) => ({
                          ...prevState,
                          euVatNumber: e.target.value,
                        }));

                        if (page_content?.eu_vat_number_validator) {
                          regex = new RegExp(
                            page_content?.eu_vat_number_validator
                          );
                        }

                        if (e.target.value !== "") {
                          setEuVatTaxVerified(regex.test(e.target.value));
                        } else {
                          setEuVatTaxVerified(false);
                        }
                      }}
                      onBlur={async (e) => {
                        if (
                          !isEUVatNumberCheckedAndCorrect &&
                          euVatTaxVerified &&
                          e.target.value.length > 0
                        ) {
                          const result = await verifyEuVatNumber(
                            countrySelected,
                            e.target.value,
                            languageCode
                          );

                          const { isValid, responseStatus } = result;
                          dispatch(setShowServerErrorMessage(responseStatus));

                          dispatch(
                            changeIsEUVatNumberCheckedAndCorrect(isValid)
                          );

                          if (
                            euVatTaxVerified &&
                            !isValid &&
                            bothVatNumbersAreMandatory
                          ) {
                            setIsEuVatNumberCorrect(isValid);
                            dispatch(changeEuVatTax(""));
                          } else if (
                            euVatTaxVerified &&
                            !isValid &&
                            !bothVatNumbersAreMandatory
                          ) {
                            dispatch(changeEuVatTax(""));
                            setShowInvalidTaxNumberModal(true);
                            setIsEuVatNumberCorrect(isValid);
                          } else if (euVatTaxVerified && isValid) {
                            setIsEuVatNumberCorrect(isValid);
                            setEuVatHasBeenVerified(true);
                            dispatch(changeEuVatTax(e.target.value));
                          }
                        }
                      }}
                    />
                  )}
                  <StyledInput
                    data-cy="company-details-street-name-and-number"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.street_name_and_number?.label}
                    required={page_content?.street_name_and_number?.required}
                    value={companyDetails.streetNameAndNumber}
                    onChange={(e) =>
                      setCompanyDetails((prevState) => ({
                        ...prevState,
                        streetNameAndNumber: e.target.value,
                      }))
                    }
                    onBlur={(e) =>
                      dispatch(changeStreetNameAndNumber(e.target.value))
                    }
                  />
                  <StyledInput
                    data-cy="company-details-city"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.city?.label}
                    required={page_content?.city?.required}
                    value={companyDetails.city}
                    onChange={(e) =>
                      setCompanyDetails((prevState) => ({
                        ...prevState,
                        city: e.target.value,
                      }))
                    }
                    onBlur={(e) => dispatch(changeCity(e.target.value))}
                  />
                  <StyledInput
                    data-cy="company-details-zip-code"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.zip_code?.label}
                    required={page_content?.zip_code?.required}
                    value={companyDetails.zipCode}
                    onChange={(e) =>
                      setCompanyDetails((prevState) => ({
                        ...prevState,
                        zipCode: e.target.value,
                      }))
                    }
                    onBlur={(e) => dispatch(changeZipCode(e.target.value))}
                  />
                  <StyledDisabledInput
                    data-cy="company-details-country"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.country?.label}
                    required={page_content?.country?.required}
                    value={companyDetails.country}
                  />
                  {page_content?.states && (
                    <StyledStateSelect
                      data-cy="company-details-states"
                      onChange={(event) =>
                        dispatch(changeStates(event.target.value))
                      }
                      label={page_content?.states?.label}
                      value={formState?.states?.value}
                    >
                      {page_content?.states?.value?.map((state) => (
                        <NewSelect.Option key={state} value={state}>
                          {state}
                        </NewSelect.Option>
                      ))}
                    </StyledStateSelect>
                  )}
                </StyledInputGroup>

                <StyledRadioContainer isMobile={isMobile}>
                  <StyledRadioGroup isMobile={isMobile}>
                    <StyledRadioLabel isMobile={isMobile}>
                      {page_content?.delivery_address?.label}
                      {page_content?.delivery_address?.required && (
                        <StyledRequired>*</StyledRequired>
                      )}
                    </StyledRadioLabel>
                    <StyledRadioButtonGroup
                      data-cy="company-details-delivery-address-radio-group"
                      onChange={(event) => {
                        dispatch(
                          changeDeliverySameAsAddress(event.target.value)
                        );
                      }}
                      name="delivery-address"
                      value={formState.delivery.same_as_address.value}
                    >
                      <NewRadioGroup.Button
                        data-cy="company-details-delivery-address-radio-button-yes"
                        value={
                          page_content?.delivery_address?.options[0]?.value
                        }
                        label={
                          page_content?.delivery_address?.options[0]?.label
                        }
                      />
                      <NewRadioGroup.Button
                        data-cy="company-details-delivery-address-radio-button-no"
                        value={
                          page_content?.delivery_address?.options[1]?.value
                        }
                        label={
                          page_content?.delivery_address?.options[1]?.label
                        }
                      />
                    </StyledRadioButtonGroup>
                  </StyledRadioGroup>

                  {formState.delivery.same_as_address.value === "no" && (
                    <StyledDeliveryAddressGroup>
                      <StyledInputGroup isMobile={isMobile}>
                        <StyledInput
                          data-cy="company-details-delivery-street-name-and-number"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.street_name_and_number?.label}
                          required={
                            page_content?.street_name_and_number?.required
                          }
                          value={companyDetails.deliveryStreetNameAndNumber}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              deliveryStreetNameAndNumber: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(
                              changeDeliveryStreetNameAndNumber(e.target.value)
                            )
                          }
                        />

                        <StyledInput
                          data-cy="company-details-delivery-city"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.city?.label}
                          required={page_content?.city?.required}
                          value={companyDetails.deliveryCity}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              deliveryCity: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changeDeliveryCity(e.target.value))
                          }
                        />

                        <StyledInput
                          data-cy="company-details-delivery-zip-code"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.zip_code?.label}
                          required={page_content?.zip_code?.required}
                          value={companyDetails.deliveryZipCode}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              deliveryZipCode: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changeDeliveryZipCode(e.target.value))
                          }
                        />

                        <StyledInput
                          data-cy="company-details-delivery-country"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.country?.label}
                          required={page_content?.country?.required}
                          value={companyDetails.deliveryCountry}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              deliveryCountry: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changeDeliveryCountry(e.target.value))
                          }
                        />
                      </StyledInputGroup>
                      <StyledCheckboxGroup isMobile={isMobile}>
                        <StyledCheckbox
                          data-cy="company-details-delivery-address-confirm"
                          onChange={(checked) =>
                            dispatch(
                              changeDeliveryAddressConfirm(
                                checked.currentTarget.checked
                              )
                            )
                          }
                          checked={formState.delivery.address_confirm.value}
                        />
                        <StyledCheckboxLabel>
                          {page_content?.confirmation?.label}
                          &nbsp;
                          <StyledHoverSpan
                            data-cy="company-details-delivery-address-external-link"
                            onClick={() => setConfirmationModal(true)}
                          >
                            {page_content?.confirmation?.external_link?.label}
                          </StyledHoverSpan>
                          {page_content?.confirmation?.required && (
                            <StyledRequired>*</StyledRequired>
                          )}
                        </StyledCheckboxLabel>
                      </StyledCheckboxGroup>
                    </StyledDeliveryAddressGroup>
                  )}

                  <StyledRadioGroup isMobile={isMobile}>
                    <StyledRadioLabel isMobile={isMobile}>
                      {page_content?.postal_address?.label}
                      {page_content?.postal_address?.required && (
                        <StyledRequired>*</StyledRequired>
                      )}
                    </StyledRadioLabel>
                    <StyledRadioButtonGroup
                      data-cy="company-details-postal-address-radio-group"
                      onChange={(event) =>
                        dispatch(changePostalSameAsAddress(event.target.value))
                      }
                      name="postal-address"
                      value={formState.postal.same_as_address.value}
                    >
                      <NewRadioGroup.Button
                        data-cy="company-details-postal-address-radio-button-yes"
                        value={page_content?.postal_address?.options[0]?.value}
                        label={page_content?.postal_address?.options[0]?.label}
                      />
                      <NewRadioGroup.Button
                        data-cy="company-details-postal-address-radio-button-no"
                        value={page_content?.postal_address?.options[1]?.value}
                        label={page_content?.postal_address?.options[1]?.label}
                      />
                    </StyledRadioButtonGroup>
                  </StyledRadioGroup>

                  {formState.postal.same_as_address.value === "no" && (
                    <StyledDeliveryAddressGroup>
                      <StyledInputGroup isMobile={isMobile}>
                        <StyledInput
                          data-cy="company-details-postal-street-name-and-number"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.street_name_and_number?.label}
                          required={
                            page_content?.street_name_and_number?.required
                          }
                          value={companyDetails.postalStreetNameAndNumber}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              postalStreetNameAndNumber: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(
                              changePostalStreetNameAndNumber(e.target.value)
                            )
                          }
                        />

                        <StyledInput
                          data-cy="company-details-postal-city"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.city?.label}
                          required={page_content?.city?.required}
                          value={companyDetails.postalCity}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              postalCity: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changePostalCity(e.target.value))
                          }
                        />

                        <StyledInput
                          data-cy="company-details-postal-zip-code"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.zip_code?.label}
                          required={page_content?.zip_code?.required}
                          value={companyDetails.postalZipCode}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              postalZipCode: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changePostalZipCode(e.target.value))
                          }
                        />

                        <StyledInput
                          data-cy="company-details-postal-country"
                          state="default"
                          colorOn="bg_white"
                          label={page_content?.country?.label}
                          required={page_content?.country?.required}
                          value={companyDetails.postalCountry}
                          onChange={(e) =>
                            setCompanyDetails((prevState) => ({
                              ...prevState,
                              postalCountry: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            dispatch(changePostalCountry(e.target.value))
                          }
                        />
                      </StyledInputGroup>
                    </StyledDeliveryAddressGroup>
                  )}
                </StyledRadioContainer>

                <StyledInputGroup isMobile={isMobile}>
                  <StyledPhoneNumberGroup>
                    <StyledNewSelect
                      data-cy="company-details-company-phone-number-prefix"
                      label="Area code"
                      onChange={(e) =>
                        dispatch(changeCompanyPhoneNumberPrefix(e.target.value))
                      }
                      value={formState.company_phone_number_prefix.value}
                    >
                      {page_content?.company_phone_number?.prefixes?.map(
                        (option) => {
                          return (
                            <NewSelect.Option
                              key={option.country_prefix_phone_code}
                              value={option.country_prefix_phone_code}
                            >
                              {option.country_prefix_phone_code}
                            </NewSelect.Option>
                          );
                        }
                      )}
                    </StyledNewSelect>
                    <StyledInput
                      data-cy="company-details-company-phone-number"
                      state={phoneNumberVerified ? "default" : "error"}
                      colorOn="bg_white"
                      message={
                        phoneNumberVerified
                          ? ""
                          : page_content?.company_phone_number_error_message_block
                      }
                      label={page_content?.company_phone_number?.label}
                      required={page_content?.company_phone_number?.required}
                      value={companyDetails.companyPhoneNumber}
                      onChange={(e) => {
                        let regex;

                        setCompanyDetails((prevState) => ({
                          ...prevState,
                          companyPhoneNumber: e.target.value,
                        }));

                        if (
                          page_content?.company_phone_number_validation_block
                        ) {
                          regex = new RegExp(
                            page_content?.company_phone_number_validation_block
                          );
                        }

                        if (e.target.value !== "") {
                          setPhoneNumberVerified(regex.test(e.target.value));
                        } else {
                          setPhoneNumberVerified(false);
                        }
                      }}
                      onBlur={(e) =>
                        dispatch(changeCompanyPhoneNumber(e.target.value))
                      }
                    />
                  </StyledPhoneNumberGroup>
                  <StyledInput
                    data-cy="company-details-company-site-or-portal"
                    state="default"
                    colorOn="bg_white"
                    label={page_content?.company_site_or_portal?.label}
                    required={page_content?.company_site_or_portal?.required}
                    value={companyDetails.companySiteOrPortal}
                    onChange={(e) =>
                      setCompanyDetails((prevState) => ({
                        ...prevState,
                        companySiteOrPortal: e.target.value,
                      }))
                    }
                    onBlur={(e) =>
                      dispatch(changeCompanySiteOrPortal(e.target.value))
                    }
                  />
                </StyledInputGroup>
              </StyledSection>
            </StyledContent>
            <Button
              backButtonText={page_content?.back_button?.label}
              backLink="#"
              backButtonDisabled={page_content?.back_button?.disable}
              continueButtonText={page_content?.continue_button?.label}
              nextLink="/contact-information"
              nextButtonDisabled={continueButtonDisabled}
              onBackClick={() => navigate(-1)}
              isMobile={vp.lessThan.md}
            />
          </>
        )}
      </StyledCompanyDetils>
    </OverlayProvider>
  );
};

export default CompanyDetails;
